import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  clientAssetsQueryOptions,
  clientContractsQueryOptions,
  clientDocumentsQueryOptions,
  clientInvoicesQueryOptions,
  clientJobsQueryOptions,
  clientPaymentsQueryOptions,
  clientQuotesQueryOptions,
  clientReportsQueryOptions,
  useClient,
} from '@/hooks/useClients'
import { usePermissions } from '@/hooks/usePermissions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { useAppSelector } from '@/store/hooks'
import { DeleteClientDialog } from '@/components/clients/DeleteClientDialog'
import { ClientDetailInfoSection } from '@/components/clients/ClientDetailInfoSection'
import { ProfileTab } from '@/components/clients/tabs/ProfileTab'
import { QuotesTab } from '@/components/clients/tabs/QuotesTab'
import { ContractsTab } from '@/components/clients/tabs/ContractsTab'
import { JobHistoryTab } from '@/components/clients/tabs/JobHistoryTab'
import { ReportsTab } from '@/components/clients/tabs/ReportsTab'
import { AssetsTab } from '@/components/clients/tabs/AssetsTab'
import { InvoicesTab } from '@/components/clients/tabs/InvoicesTab'
import { PaymentsTab } from '@/components/clients/tabs/PaymentsTab'
import { DocumentsTab } from '@/components/clients/tabs/DocumentsTab'
import { JobDetailDrawer } from '@/components/jobs/JobDetailDrawer'
import { PaymentDrawerShell } from '@/components/payments/PaymentDrawerShell'
import { PaymentDetailDrawerContent } from '@/components/payments/PaymentDetailDrawerContent'
import { primaryEmail } from '@/lib/clientContacts'
import { DEFAULT_PAGE_SIZE } from '@/lib/listFetch'
import type { ListQuery } from '@/types'

// Tab values are stable identifiers; labels are resolved via i18n at render time.
// Order matches Figma 2889:56744 (Tag Toggle Group).
const TAB_VALUES = [
  'profile',
  'jobs',
  'quotes',
  'contracts',
  'invoices',
  'payments',
  'reports',
  'assets',
  'documents',
] as const

type ClientDetailTab = (typeof TAB_VALUES)[number]

const INITIAL_CLIENT_TAB_QUERY = {
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
  search: undefined,
} satisfies ListQuery

// Figma 2889:56659 — page shell (bg-2, 24px padding, 16px section gap).
const pageShellClass =
  '-mx-6 -mt-6 flex min-h-full flex-col gap-6 bg-[#f4f5f8] px-6 pb-6 pt-6'

// Figma 2889:56744 — Tag Toggle Group on the page background.
const tabBarShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

const tabTriggerClass =
  'shrink-0 rounded-lg border-0 px-3 py-1.5 text-sm font-medium leading-none text-[#3d434e] shadow-none transition-colors hover:text-[#171a20] data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white data-[state=active]:shadow-none'

// 2.4.6 Client details — detail info header (Figma 2889:56660) + tabbed content.
export default function ClientDetailPage() {
  const { t } = useTranslation('clients')
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAppSelector(s => s.auth.user?.role)
  const isAdmin = role === 'Admin'
  const { canDelete, canEdit } = usePermissions()
  const canEditClient = canEdit('clients')
  const canCreateJob = canEdit('agenda')
  const canCreateQuote = canEdit('quotes')
  const canCreateContract = canEdit('contracts')
  const canCreateInvoice = canEdit('invoices')
  const canCreatePayment = canEdit('payments')
  const [searchParams, setSearchParams] = useSearchParams()
  const [showDelete, setShowDelete] = useState(false)

  const { data, isLoading, isError } = useClient(id)
  const client = data?.data
  useBreadcrumbLabel(client?.name)

  // Fall back to Profile for unknown tabs (e.g. retired ?tab=notes or activity).
  const requestedTab = searchParams.get('tab') ?? 'profile'
  const activeTab = (TAB_VALUES as readonly string[]).includes(requestedTab)
    ? requestedTab
    : 'profile'
  const setTab = (tab: string) => setSearchParams({ tab }, { replace: true })
  const preloadTab = (tab: ClientDetailTab) => {
    if (tab === 'profile' || tab === activeTab) return

    switch (tab) {
      case 'jobs':
        void queryClient.prefetchQuery(
          clientJobsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'quotes':
        void queryClient.prefetchQuery(
          clientQuotesQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'contracts':
        void queryClient.prefetchQuery(
          clientContractsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'invoices':
        void queryClient.prefetchQuery(
          clientInvoicesQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'payments':
        void queryClient.prefetchQuery(
          clientPaymentsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'reports':
        void queryClient.prefetchQuery(
          clientReportsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'assets':
        void queryClient.prefetchQuery(
          clientAssetsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
      case 'documents':
        void queryClient.prefetchQuery(
          clientDocumentsQueryOptions(id, INITIAL_CLIENT_TAB_QUERY)
        )
        break
    }
  }
  const selectedJobId = searchParams.get('job')
  const selectedPaymentId = searchParams.get('payment')
  const openJobDrawer = (jobId: string, tab?: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('job', jobId)
        next.delete('payment')
        if (tab) next.set('job_tab', tab)
        else next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }
  const openPaymentDrawer = (paymentId: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('payment', paymentId)
        next.delete('job')
        next.delete('job_tab')
        return next
      },
      { replace: false }
    )
  }
  const closeDetailDrawer = () => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('job')
        next.delete('job_tab')
        next.delete('payment')
        return next
      },
      { replace: false }
    )
  }

  if (isLoading) {
    return (
      <div className={pageShellClass}>
        <Skeleton className='h-[242px] w-full rounded-2xl' />
        <Skeleton className='h-[50px] w-full rounded-xl' />
        <Skeleton className='h-96 w-full rounded-2xl' />
      </div>
    )
  }

  if (isError || !client) {
    return (
      <div className={pageShellClass}>
        <div className='space-y-4'>
          <p className='text-destructive'>{t('detail.notFound')}</p>
          <Button variant='outline' onClick={() => navigate('/clients')}>
            {t('detail.backToClients')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageShellClass}>
      <ClientDetailInfoSection
        client={client}
        canDeleteClient={isAdmin && canDelete('clients')}
        canEditClient={canEdit('clients')}
        onDelete={() => setShowDelete(true)}
      />

      <Tabs
        value={activeTab}
        onValueChange={setTab}
        className='flex flex-col gap-4'
      >
        <div
          className={`w-full overflow-x-auto rounded-xl border border-[#e7e9ef] bg-white p-2 ${tabBarShadow}`}
        >
          <TabsList className='h-auto w-max justify-start gap-2 overflow-x-visible border-0 bg-transparent p-0'>
            {TAB_VALUES.map(value => (
              <TabsTrigger
                key={value}
                value={value}
                onPointerEnter={() => preloadTab(value)}
                onFocus={() => preloadTab(value)}
                className={tabTriggerClass}
              >
                {t(`detail.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value='profile' className='mt-0'>
          <ProfileTab
            client={client}
            canEditClient={canEditClient}
            onOpenJob={openJobDrawer}
            onOpenPayment={openPaymentDrawer}
          />
        </TabsContent>
        <TabsContent value='jobs' className='mt-0'>
          <JobHistoryTab
            clientId={id}
            canCreateJob={canCreateJob}
            onOpenJob={openJobDrawer}
          />
        </TabsContent>
        <TabsContent value='quotes' className='mt-0'>
          <QuotesTab clientId={id} canCreateQuote={canCreateQuote} />
        </TabsContent>
        <TabsContent value='contracts' className='mt-0'>
          <ContractsTab clientId={id} canCreateContract={canCreateContract} />
        </TabsContent>
        <TabsContent value='invoices' className='mt-0'>
          <InvoicesTab
            clientId={id}
            clientEmail={primaryEmail(client) ?? ''}
            canCreateInvoice={canCreateInvoice}
          />
        </TabsContent>
        <TabsContent value='payments' className='mt-0'>
          <PaymentsTab
            clientId={id}
            canCreatePayment={canCreatePayment}
            onOpenJob={openJobDrawer}
            onOpenPayment={openPaymentDrawer}
          />
        </TabsContent>
        <TabsContent value='reports' className='mt-0'>
          <ReportsTab
            clientId={id}
            clientEmail={primaryEmail(client) ?? ''}
            onOpenJobReport={jobId => openJobDrawer(jobId, 'reports')}
          />
        </TabsContent>
        <TabsContent value='assets' className='mt-0'>
          <AssetsTab clientId={id} />
        </TabsContent>
        <TabsContent value='documents' className='mt-0'>
          <DocumentsTab clientId={id} canEditClient={canEditClient} />
        </TabsContent>
      </Tabs>

      <DeleteClientDialog
        client={showDelete ? client : null}
        onClose={() => setShowDelete(false)}
        onDeleted={() => navigate('/clients')}
      />

      <JobDetailDrawer
        jobId={selectedJobId}
        initialTab={searchParams.get('job_tab') ?? undefined}
        onClose={closeDetailDrawer}
      />

      <PaymentDrawerShell
        open={!!selectedPaymentId}
        title={t('payments.drawerTitle', { defaultValue: 'Payment details' })}
        onClose={closeDetailDrawer}
      >
        {selectedPaymentId ? (
          <PaymentDetailDrawerContent
            key={selectedPaymentId}
            paymentId={selectedPaymentId}
            onClose={closeDetailDrawer}
          />
        ) : null}
      </PaymentDrawerShell>
    </div>
  )
}
